import * as React from 'react'
import Question from './Question'
import Input, { InputAPI } from './Input'
import Footer from './Footer'
import Response from './Response'
import { OpenedSurvey } from './types'
import Spinner from './Spinner'
import CloseButton from './CloseButton'
import { useChat } from './use-chat'
import { useOffsetContainerCtx } from './OffsetContainer'
import { cn } from './cn'
import { isMobile } from 'react-device-detect'
import Buttons from './Buttons'

export type ChatProps = {
  survey: OpenedSurvey
  setOpened: (opened: boolean) => void
  onInputFocus?: () => void
  onInputBlur?: () => void
  onHeightChange?: (height: number) => void
  autoFocus?: boolean
}

const Chat = React.forwardRef<InputAPI, ChatProps>(
  (
    { survey, setOpened, onInputFocus, onInputBlur, onHeightChange, autoFocus },
    ref
  ) => {
    const chat = useChat(survey, onHeightChange)
    const hasMounted = React.useRef(false)

    const scrollViewRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => {
      if (!hasMounted.current) {
        hasMounted.current = true
        return
      }

      if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({
          top: scrollViewRef.current.scrollHeight,
          behavior: 'smooth',
        })
      }
    }, [chat.loading])

    const [inputFocused, setInputFocused] = React.useState(false)

    const isTouched = useOffsetContainerCtx(s => s.isTouched)
    const device = useOffsetContainerCtx(s => s.device)

    const isFullscreen = device === 'default' && isTouched

    const useMinHeight =
      !isFullscreen &&
      ((chat.history.length === 1 && chat.loading) || chat.history.length > 1)

    const buttonsVisible =
      chat.lastQuestion.choices.length > 0 && !chat.loading && !chat.finished

    const inputVisible = !chat.finished && !buttonsVisible

    return (
      <div
        className={`sp-isolate sp-flex sp-flex-col sp-relative sp-h-full sp-flex-grow sp-flex-shrink-0 sp-w-full`}>
        <CloseButton
          close={() => setOpened(false)}
          isInputFocused={inputFocused}
        />
        <div
          className={cn(
            'sp-bg-survey-background sp-flex sp-flex-col sp-flex-1 sp-h-full sp-overflow-y-hidden',
            {
              'sp-shadow-survey-shadow sp-rounded-[10px] sp-max-h-[400px] sp-pb-2':
                !isFullscreen,
              'sp-min-h-[400px]': useMinHeight,
              'sp-pb-3': isFullscreen,
            }
          )}>
          <div
            ref={scrollViewRef}
            className="sp-overflow-y-auto sp-flex sp-flex-col sp-flex-grow">
            <div
              className={cn(
                'sp-flex sp-flex-col sp-gap-5 sp-flex-grow sp-px-5',
                {
                  'sp-justify-end': isMobile,
                  'sp-pt-5': !isFullscreen,
                }
              )}>
              {chat.history.map(({ type, text }, i) =>
                type === 'question' ? (
                  <Question key={i}>{text}</Question>
                ) : (
                  <Response key={i}>{text}</Response>
                )
              )}
              {chat.error && (
                <Response>
                  We are sorry, but something went wrong on our end.
                </Response>
              )}
              {chat.loading && (
                <div className="sp-h-[30px]">
                  <Spinner />
                </div>
              )}
            </div>
            {buttonsVisible && (
              <Buttons
                onSubmit={chat.addAnswer}
                onFocus={() => {
                  setInputFocused(true)
                  onInputFocus?.()
                }}
                onBlur={() => {
                  setInputFocused(false)
                  onInputBlur?.()
                }}
                questionId={chat.lastQuestion.id}
                choices={chat.lastQuestion.choices}
                loadingResponse={chat.loading}
              />
            )}
          </div>

          <div className="sp-flex-shrink-0 sp-flex-grow-0 sp-gap-1.5 sp-flex sp-flex-col">
            {inputVisible && (
              <Input
                ref={ref}
                onSubmit={chat.addAnswer}
                onFocus={() => {
                  setInputFocused(true)
                  onInputFocus?.()
                }}
                onBlur={() => {
                  setInputFocused(false)
                  onInputBlur?.()
                }}
                questionId={chat.lastQuestion.id}
                role={chat.lastQuestion.role}
                loadingResponse={chat.loading}
                hasMounted={hasMounted}
                autoFocus={autoFocus}
              />
            )}
            <Footer
              chatFinished={chat.finished}
              surveyId={survey.session.surveyId}
              workspaceId={survey.session.workspaceId}
            />
          </div>
        </div>
      </div>
    )
  }
)

export default Chat
